import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';

import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { FormatDatasetAdapter } from 'app/model/adapters/datasets/format-dataset.adapter';
import { ExcelDataset } from 'app/model/dataset/excel.dataset';
import { compressionAdapts } from './file-type.dataset.adapter';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ExcelDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            schema: '*',
            parameters: '*',
            type: '*',
            linkedServiceName: '*',
            linkedService: '*',
            connectionSettings: __IS_FABRIC_DPE__ ? '*' : undefined,
            connectionProperties: GlobalVariables.disableServerCertificate ? '*' : undefined,
            typeProperties: {
                location: '*',
                sheetName: '*',
                sheetIndex: '*',
                range: '*',
                firstRowAsHeader: '*',
                compression: compressionAdapts,
                nullValue: '*'
            }
        }
    }
})
export class ExcelDatasetAdapter extends FormatDatasetAdapter<ExcelDataset, Model.DatasetV2Resource> {
    protected async _toResource(dataset: ExcelDataset): Promise<Model.DatasetV2Resource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetV2Resource;
        const modelDataset = this.properties(modelDatasetResource) as Model.ExcelDataset;
        modelDataset.type = Metamodel.modelFor(ExcelDataset).name;
        modelDataset.typeProperties = modelDataset.typeProperties || {
            sheetName: undefined
        };
        if (dataset.location) {
            modelDataset.typeProperties.location = this.locationToResource(dataset);
        }
        if (!dataset.useSheetName) {
            modelDataset.typeProperties.sheetIndex = ValueAdapter.toResource(dataset, ExcelDataset.SHEET_INDEX);
        } else {
            modelDataset.typeProperties.sheetName = ValueAdapter.toResource(dataset, ExcelDataset.SHEET_NAME);
        }
        modelDataset.typeProperties.range = ValueAdapter.toResource(dataset, ExcelDataset.RANGE);
        modelDataset.typeProperties.firstRowAsHeader = ValueAdapter.toResource(dataset, ExcelDataset.FIRSTROW_ASHEADER);
        const compression = ValueAdapter.toResource(dataset, ExcelDataset.COMPRESSION_CODEC);
        if (compression != null) {
            modelDataset.typeProperties.compression = {
                type: compression,
                level: ValueAdapter.toResource(dataset, ExcelDataset.COMPRESSION_LEVEL)
            };
        }
        modelDataset.typeProperties.nullValue = ValueAdapter.toResource(dataset, ExcelDataset.NULL_VALUE) || undefined;
        modelDataset.schema = this.columnValueToResource(dataset, dataset.columns);
        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetV2Resource, dataset?: ExcelDataset): Promise<ExcelDataset> {
        if (!dataset) {
            dataset = ExcelDataset.MODEL.create<ExcelDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        if (modelDatasetResource
            && modelDatasetResource.properties
            && modelDatasetResource.properties.typeProperties
            && modelDatasetResource.properties.typeProperties.location) {
            await this.locationToModel(modelDatasetResource, dataset);
        }

        if (modelDatasetResource
            && modelDatasetResource.properties
            && modelDatasetResource.properties.typeProperties) {
            const typeProps = modelDatasetResource.properties.typeProperties as Model.ExcelDatasetTypeProperties;
            if (typeProps.sheetIndex != null) {
                dataset.useSheetName = false;
                ValueAdapter.toModel(dataset, ExcelDataset.SHEET_INDEX, typeProps.sheetIndex);
            } else {
                dataset.useSheetName = true;
                ValueAdapter.toModel(dataset, ExcelDataset.SHEET_NAME, typeProps.sheetName);
            }
            ValueAdapter.toModel(dataset, ExcelDataset.RANGE, typeProps.range);
            ValueAdapter.toModel(dataset, ExcelDataset.FIRSTROW_ASHEADER, typeProps.firstRowAsHeader);
            ValueAdapter.toModel(dataset, ExcelDataset.NULL_VALUE, typeProps.nullValue);
            if (modelDatasetResource.properties.typeProperties.compression != null) {
                ValueAdapter.toModel(dataset, ExcelDataset.COMPRESSION_CODEC, modelDatasetResource.properties.typeProperties.compression.type);
                ValueAdapter.toModel(dataset, ExcelDataset.COMPRESSION_LEVEL, modelDatasetResource.properties.typeProperties.compression.level);
            }
        }

        dataset.columns = this.columnResourceToValue(dataset, modelDatasetResource.properties.schema);

        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, ExcelDataset.MODEL));
        return dataset;
    }
}
